/**
 * BaseStepWidget - Widget de base pour afficher une étape
 * Identique à Flutter BaseStepWidget
 */

import React, {useState, useRef, useEffect, useMemo} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Platform,
  useWindowDimensions,
  Animated,
  Easing,
  Switch,
  Alert,
} from 'react-native';
import Svg, {Path} from 'react-native-svg';
// Markdown sera géré différemment, pour l'instant on utilise du Text simple
import {PlayerStep} from '../models/PlayerStep';
import {PlayerPath} from '../models/PlayerPath';
import {PlayerAction} from '../models/PlayerAction';
import {usePlayer} from '../contexts/PlayerContext';
import {useTheme} from '../contexts/ThemeContext';
import {GameStateStatus} from '../models/GameState';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faArrowRight, faShoppingBag, faScroll, faBookOpen, faInfoCircle, faArrowLeft} from '@fortawesome/free-solid-svg-icons';
import {Modal} from 'react-native';
import {Fonts} from '../config/fonts';
import conditionHelper from '../helpers/ConditionHelper';
import {SolidObject} from '../models/SolidObject';
import GameRegistry from '../systems/GameRegistry';
import StepPaths from './StepPaths';
import {Theme} from '../theme/theme';

interface BaseStepWidgetProps {
  step: PlayerStep;
}

const BaseStepWidget: React.FC<BaseStepWidgetProps> = ({step}) => {
  const {gameState, choosePath, executeAction, currentAdventure, editorMode, setEditorMode, goToPreviousStep} = usePlayer();
  const {theme, isDarkMode, isReadingMode} = useTheme();
  const {width} = useWindowDimensions();
  const scrollViewRef = useRef<ScrollView>(null);
  const [showObjectDialog, setShowObjectDialog] = useState<SolidObject | null>(null);
  const [showExamineDialog, setShowExamineDialog] = useState<SolidObject | null>(null);
  const [showConditionsDialog, setShowConditionsDialog] = useState<{path: PlayerPath; conditions: string[]} | null>(null);
  
  // Générer les styles basés sur le thème ET mode lecture
  const styles = useMemo(() => createStyles(theme, isReadingMode), [theme, isReadingMode]);
  
  // Vérifier si un objet est dans l'inventaire
  const isObjectInInventory = (objectId: string): boolean => {
    return gameState?.data.objects?.some(
      (obj) => obj.attributeId === objectId && obj.dropped === 0,
    ) || false;
  };

  const isMobile = width <= 600;
  
  // Détecter le mode édition (popup)
  // @ts-ignore - window est disponible en web
  const isEditingMode = Platform.OS === 'web' && typeof window !== 'undefined' && window.opener !== null;
  
  // Vérifier si c'est une étape de fin
  const isEndingStep = 
    gameState?.state === GameStateStatus.FAILED || 
    gameState?.state === GameStateStatus.SUCCEED ||
    step.type?.toUpperCase() === 'STEP_FAILED' || 
    step.type?.toUpperCase() === 'STEP_SUCCEED' ||
    step.type?.toUpperCase() === 'FAILED' ||
    step.type?.toUpperCase() === 'SUCCEED';
  
  // Créer un mapping entre les paths filtrés et leurs indices originaux
  const pathIndexMapping = useRef<Map<number, number>>(new Map());
  
  // Obtenir les paths originaux (tous les paths avant filtrage) pour le mode éditeur
  const originalPaths = step.allPaths || step.paths || [];
  
  // Calculer les paths disponibles pour le mapping
  const availablePaths = useMemo(() => {
    if (!gameState) {
      return originalPaths;
    }
    const cheatValue = Boolean(editorMode);
    const stepWithAllPaths = {
      ...step,
      paths: originalPaths,
    };
    return conditionHelper.filterPaths(stepWithAllPaths, gameState, cheatValue);
  }, [gameState, step, editorMode, originalPaths]);
  
  // Reconstruire le mapping quand les paths changent
  // Le mapping : filteredIndex -> originalIndex dans originalPaths
  useEffect(() => {
    if (gameState && originalPaths && availablePaths.length > 0) {
      const mapping = new Map<number, number>();
      
      availablePaths.forEach((filteredPath, filteredIndex) => {
        const originalIndex = originalPaths.findIndex((originalPath) => {
          const idMatch = filteredPath.id && originalPath.id && filteredPath.id === originalPath.id;
          const descMatch = 
            filteredPath.description === originalPath.description && 
            filteredPath.child === originalPath.child;
          return idMatch || descMatch;
        });
        
        if (originalIndex !== -1) {
          const alreadyMappedTo = Array.from(mapping.entries()).find(([fi, oi]) => oi === originalIndex)?.[0];
          if (alreadyMappedTo === undefined) {
            mapping.set(filteredIndex, originalIndex);
          }
        }
      });
      
      pathIndexMapping.current = mapping;
    } else {
      pathIndexMapping.current = new Map();
    }
  }, [availablePaths, originalPaths, gameState]);
  
  // Filtrer les actions selon les conditions
  const availableActions = gameState
    ? conditionHelper.filterActions(step, gameState)
    : step.actions || [];

  // Animations en décalé pour chaque élément
  const descriptionOpacity = useRef(new Animated.Value(0)).current;
  const descriptionTranslateY = useRef(new Animated.Value(20)).current;
  
  // Créer les animations pour les actions
  const [actionsAnimations, setActionsAnimations] = useState<Array<{
    opacity: Animated.Value;
    translateY: Animated.Value;
  }>>([]);

  // Créer les animations quand le step change
  useEffect(() => {
    // Créer les animations pour les actions
    const newActionsAnimations = availableActions.map(() => ({
      opacity: new Animated.Value(0),
      translateY: new Animated.Value(20),
    }));
    setActionsAnimations(newActionsAnimations);

    // Réinitialiser l'animation de la description
    descriptionOpacity.setValue(0);
    descriptionTranslateY.setValue(20);
  }, [step.numero, availableActions.length]);

  // Lancer les animations après qu'elles soient créées
  useEffect(() => {
    // TOUJOURS animer la description
    Animated.parallel([
      Animated.timing(descriptionOpacity, {
        toValue: 1,
        duration: 600,
        delay: 200,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }),
      Animated.timing(descriptionTranslateY, {
        toValue: 0,
        duration: 600,
        delay: 200,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }),
    ]).start();

    // Animer les actions en décalé
    actionsAnimations.forEach((anim, index) => {
      Animated.parallel([
        Animated.timing(anim.opacity, {
          toValue: 1,
          duration: 600,
          delay: 200 * index,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(anim.translateY, {
          toValue: 0,
          duration: 600,
          delay: 200 * index,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start();
    });
  }, [step.numero, actionsAnimations.length]);

  const handlePathPress = async (pathIndex: number) => {
    if (!gameState) {
      console.error('[BaseStepWidget] No gameState available');
      return;
    }
    
    try {
      // pathIndex est l'index dans availablePaths (filtrés en mode normal, tous les paths en mode éditeur)
      const filteredPath = availablePaths[pathIndex];
      if (!filteredPath) {
        console.error(`[BaseStepWidget] Invalid path index: ${pathIndex} (availablePaths length: ${availablePaths.length})`);
        return;
      }
      
      console.log(`[BaseStepWidget] ===== handlePathPress START =====`);
      console.log(`[BaseStepWidget] pathIndex (in availablePaths): ${pathIndex}`);
      console.log(`[BaseStepWidget] editorMode: ${editorMode}`);
      console.log(`[BaseStepWidget] availablePaths count: ${availablePaths.length}`);
      console.log(`[BaseStepWidget] originalPaths count: ${originalPaths.length}`);
      console.log(`[BaseStepWidget] step.paths count: ${step.paths.length}`);
      
      let indexToUse: number;
      
      if (editorMode) {
        // EN MODE ÉDITEUR : convertir l'index filtré en index original
        // car choosePath en mode éditeur attend l'index dans allPaths
        const originalIndex = pathIndexMapping.current.get(pathIndex);
        
        if (originalIndex === undefined) {
          // Fallback : chercher directement dans originalPaths
          console.warn(`[BaseStepWidget] Mapping not found for filtered index ${pathIndex}, using fallback search`);
          
          let fallbackIndex = -1;
          if (filteredPath.id) {
            fallbackIndex = originalPaths.findIndex((p) => p.id === filteredPath.id);
          }
          
          if (fallbackIndex === -1) {
            fallbackIndex = originalPaths.findIndex((p) => {
              const descMatch = p.description === filteredPath.description;
              const childMatch = p.child === filteredPath.child;
              return descMatch && childMatch;
            });
          }
          
          if (fallbackIndex === -1) {
            console.error(`[BaseStepWidget] Could not find original path index for filtered path at index ${pathIndex}`);
            return;
          }
          
          indexToUse = fallbackIndex;
        } else {
          indexToUse = originalIndex;
        }
        
        console.log(`[BaseStepWidget] CHEAT MODE: Using original index ${indexToUse} (from filtered index ${pathIndex})`);
      } else {
        // EN MODE NORMAL : utiliser directement l'index filtré
        // car choosePath en mode normal attend l'index dans step.paths (filtrés)
        indexToUse = pathIndex;
        console.log(`[BaseStepWidget] NORMAL MODE: Using filtered index ${indexToUse} directly`);
      }
      
      await choosePath(indexToUse);
      console.log(`[BaseStepWidget] ===== handlePathPress SUCCESS =====`);
    } catch (error: any) {
      console.error('[BaseStepWidget] ===== handlePathPress ERROR =====');
      console.error('[BaseStepWidget] Error choosing path:', error);
      console.error('[BaseStepWidget] Error stack:', error.stack);
    }
  };

  const handleActionPress = async (action: PlayerAction) => {
    try {
      const actionTypeName = action.typeName || action.type || action.id;
      if (!actionTypeName) {
        throw new Error('Action typeName not found');
      }
      await executeAction(actionTypeName, action.objects || {});
    } catch (error: any) {
      console.error('[BaseStepWidget] Error executing action:', error);
    }
  };

  // Scroll handler (optionnel, pour scroll-to-top après changement de step)
  const handleScroll = (event: any) => {
    // Scroll tracking optionnel
  };

  // Vérifier si on est en état FAILED (soit par gameState.state, soit par step.type)
  // IMPORTANT: Le gameState.state peut être FAILED même si l'étape n'est pas de type FAILED
  // (ex: après une bataille D6 où le personnage arrive à 0 PV)
  const isFailedStep = 
    gameState?.state === GameStateStatus.FAILED || 
    step.type?.toUpperCase() === 'STEP_FAILED' || 
    step.type?.toUpperCase() === 'FAILED';

  // Pour les étapes FAILED, ne PAS afficher les paths normaux
  const shouldShowPaths = !isFailedStep;

  // Gérer les actions de fin (Rembobiner / Recommencer) pour les étapes FAILED
  const handleRestart = async () => {
    try {
      await executeAction('restart', {});
    } catch (error: any) {
      console.error('[BaseStepWidget] Error executing restart:', error);
    }
  };

  const handleUseBackup = async () => {
    try {
      await executeAction('use_backup', {});
    } catch (error: any) {
      console.error('[BaseStepWidget] Error executing use_backup:', error);
    }
  };

  // Déterminer si on doit afficher le bouton "Rembobiner"
  // Le bouton "Rembobiner" s'affiche si : gameState.state === FAILED ET hasBackup ET isBackupAllowed
  const hasBackup = gameState?.hasBackup || false;
  const isBackupAllowed = currentAdventure?.isBackupAllowed ?? true;
  const shouldShowRembobiner = isFailedStep && hasBackup && isBackupAllowed;
  
  // Le bouton "Recommencer" s'affiche TOUJOURS si gameState.state === FAILED
  // (même si l'étape n'est pas de type FAILED, ex: après une bataille D6)
  const shouldShowRestart = isFailedStep;

  const buildImageUri = (imageData?: string): string | null => {
    if (!imageData) return null;
    if (imageData.startsWith('data:image')) {
      return imageData;
    }
    return `data:image/png;base64,${imageData}`;
  };

  const imageUri = buildImageUri(step.image);

  return (
    <View style={styles.container}>
      {imageUri && (
        <View style={styles.imageContainer}>
          <Image source={{uri: imageUri}} style={styles.image} resizeMode="cover" />
        </View>
      )}

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        onScroll={handleScroll}
        scrollEventThrottle={16}>
        <View style={[styles.content, isMobile ? styles.contentMobile : styles.contentDesktop]}>
          {/* Le titre est maintenant affiché en haut fixe dans PlayScreen, pas ici */}
          {/* Description - TODO: Support Markdown complet */}
          <Animated.View
            style={{
              opacity: descriptionOpacity,
              transform: [{translateY: descriptionTranslateY}],
            }}>
            <View style={styles.descriptionContainer}>
              <Text style={styles.description}>{step.description || ''}</Text>
            </View>
          </Animated.View>

          {/* Actions */}
          {availableActions.length > 0 && (
            <View style={styles.actionsContainer}>
              {availableActions.map((action, index) => {
                const anim = actionsAnimations[index];
                if (!anim) {
                  // Fallback si l'animation n'est pas encore créée
                  return (
                    <TouchableOpacity
                      key={action.id || index}
                      style={styles.actionButton}
                      activeOpacity={0.85}
                      onPress={() => handleActionPress(action)}>
                      <Text style={styles.actionText}>{action.title || action.id}</Text>
                      {action.description && (
                        <Text style={styles.actionDescription}>{action.description}</Text>
                      )}
                    </TouchableOpacity>
                  );
                }
                return (
                  <Animated.View
                    key={action.id || index}
                    style={{
                      opacity: anim.opacity,
                      transform: [{translateY: anim.translateY}],
                    }}>
                    <TouchableOpacity
                      style={styles.actionButton}
                      activeOpacity={0.85} // Variation subtile de luminosité
                      onPress={() => handleActionPress(action)}>
                      <Text style={styles.actionText}>{action.title || action.id}</Text>
                      {action.description && (
                        <Text style={styles.actionDescription}>{action.description}</Text>
                      )}
                    </TouchableOpacity>
                  </Animated.View>
                );
              })}
            </View>
          )}

          {/* Boutons de fin (FAILED) : Rembobiner et Recommencer */}
          {/* Afficher si gameState.state === FAILED (même si l'étape n'est pas de type FAILED) */}
          {shouldShowRestart && (
            <View style={styles.decisionZone}>
              <View style={styles.decisionZoneContent}>
                {/* Bouton Rembobiner (si backup disponible ET si l'aventure permet le backup) */}
                {shouldShowRembobiner && (
                  <TouchableOpacity
                    style={styles.failedActionButton}
                    activeOpacity={0.85}
                    onPress={handleUseBackup}>
                    <FontAwesomeIcon icon={faScroll} size={20} color="#fff" style={{marginRight: 12}} />
                    <Text style={styles.failedActionButtonText}>Rembobiner</Text>
                  </TouchableOpacity>
                )}
                
                {/* Bouton Recommencer (toujours affiché si gameState.state === FAILED) */}
                <TouchableOpacity
                  style={[styles.failedActionButton, styles.restartButton]}
                  activeOpacity={0.85}
                  onPress={handleRestart}>
                  <FontAwesomeIcon icon={faBookOpen} size={20} color="#fff" style={{marginRight: 12}} />
                  <Text style={styles.failedActionButtonText}>Recommencer l'aventure</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Ornement séparateur léger avant les choix */}
          {shouldShowPaths && !isReadingMode && (
            <View style={styles.pathsSeparatorContainer}>
              <Svg width="100" height="30" viewBox="0 0 100 30" style={{opacity: 0.4}}>
                {/* Ornement fin et léger */}
                <Path
                  d="M 10 15 Q 30 10 50 15 Q 70 20 90 15"
                  fill="none"
                  stroke={isDarkMode ? '#8B7355' : '#8B7355'}
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                <Path
                  d="M 45 15 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0 M 50 15 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0 M 55 15 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0"
                  fill={isDarkMode ? '#8B7355' : '#8B7355'}
                />
              </Svg>
            </View>
          )}
          
          {/* Zone de décision - Séparée du texte narratif (NE PAS afficher si FAILED) */}
          {shouldShowPaths && (
            <StepPaths
              step={step}
              gameState={gameState || undefined}
              editorMode={editorMode || false}
              onPathPress={handlePathPress}
              onShowConditions={(path, conditions) => setShowConditionsDialog({path, conditions})}
            />
          )}

          {/* Bouton "Revenir à l'étape précédente" en mode éditeur - Affiché comme un dernier path */}
          {isEditingMode && editorMode && gameState && gameState.data.recordedPaths && (() => {
            const recordedPaths = gameState.data.recordedPaths || '';
            const paths = recordedPaths ? recordedPaths.split('-') : [];
            const canGoBack = paths.length >= 2;
            return canGoBack ? (
              <TouchableOpacity
                style={styles.cheatBackPathButton}
                onPress={async () => {
                  try {
                    await goToPreviousStep();
                  } catch (error: any) {
                    console.error('[BaseStepWidget] Error going to previous step:', error);
                    // @ts-ignore - window est disponible en web
                    if (Platform.OS === 'web' && typeof window !== 'undefined') {
                      const msg = error?.message || 'Erreur lors du retour en arrière';
                      // @ts-ignore - window.alert est disponible en web
                      window.alert(msg);
                    } else {
                      Alert.alert('Erreur', error?.message || 'Erreur lors du retour en arrière');
                    }
                  }
                }}
                activeOpacity={0.7}>
                <View style={styles.cheatBackPathIndicator} />
                <View style={styles.cheatBackPathContent}>
                  <FontAwesomeIcon icon={faArrowLeft} size={16} color={theme.colors.textLight} style={{marginRight: 10, opacity: 0.6}} />
                  <Text style={styles.cheatBackPathText}>Revenir à l'étape précédente</Text>
                </View>
              </TouchableOpacity>
            ) : null;
          })()}

          {/* Objets ramassables */}
          {step.objects && step.objects.length > 0 && (
            <View style={styles.objectsContainer}>
              <View style={styles.objectsHeader}>
                <FontAwesomeIcon icon={faShoppingBag} size={18} color="#6B7280" />
                <Text style={styles.objectsTitle}>Objets ramassables :</Text>
              </View>
              <View style={styles.objectsList}>
                {step.objects.map((obj, index) => {
                  const imageUri = buildImageUri(obj.image);
                  return (
                    <TouchableOpacity
                      key={obj.id || index}
                      style={styles.objectChip}
                      activeOpacity={0.85}
                      onPress={() => setShowObjectDialog(obj)}>
                      {imageUri ? (
                        <Image source={{uri: imageUri}} style={styles.objectChipImage} />
                      ) : (
                        <View style={styles.objectChipIcon}>
                          <FontAwesomeIcon icon={faShoppingBag} size={16} color="#6B7280" />
                        </View>
                      )}
                      <Text style={styles.objectChipText}>{obj.name}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}

        </View>
      </ScrollView>
      
      {/* ObjectDetailsDialog - sera créé plus tard */}
      {showObjectDialog && (
        <View style={styles.objectDialogPlaceholder}>
          {/* TODO: Remplacer par ObjectDetailsDialog */}
          <Text>Object Dialog: {showObjectDialog.name}</Text>
        </View>
      )}
      
      {/* Modal pour afficher les conditions des paths cachés */}
      <Modal
        visible={showConditionsDialog !== null}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowConditionsDialog(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Conditions nécessaires</Text>
              <TouchableOpacity
                onPress={() => setShowConditionsDialog(null)}
                style={styles.modalCloseButton}>
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.modalBody}>
              {showConditionsDialog && showConditionsDialog.conditions.length > 0 ? (
                showConditionsDialog.conditions.map((condition, index) => (
                  <View key={index} style={styles.conditionItem}>
                    <Text style={styles.conditionText}>{condition}</Text>
                  </View>
                ))
              ) : (
                <Text style={styles.conditionText}>Aucune condition</Text>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Fonction pour créer les styles basés sur le thème ET mode lecture
const createStyles = (theme: Theme, isReadingMode: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent', // Transparent pour laisser voir l'ImageBackground
  },
  imageContainer: {
    width: '100%',
    height: 200,
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  scrollView: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollContent: {
    paddingBottom: isReadingMode ? theme.spacing.xxxl + 16 : theme.spacing.xl,
    backgroundColor: 'transparent',
  },
  content: {
    paddingHorizontal: isReadingMode ? theme.spacing.xxl : theme.spacing.xxl,
    paddingVertical: isReadingMode ? theme.spacing.xxl : theme.spacing.xl,
    backgroundColor: 'transparent',
    // En mode lecture : centrage et max-width optimal (comme un livre)
    ...(isReadingMode && {
      maxWidth: 720,
      alignSelf: 'center',
      width: '100%',
    }),
  },
  contentMobile: {
    paddingHorizontal: theme.spacing.xl,
  },
  contentDesktop: {
    paddingHorizontal: isReadingMode ? theme.spacing.xxxl : 64,
    maxWidth: isReadingMode ? 680 : 800,
    backgroundColor: 'transparent',
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 24,
    textAlign: 'center',
    color: theme.colors.text,
  },
  titleMobile: {
    fontSize: 20,
  },
  actionsContainer: {
    marginBottom: 32,
    marginTop: 24,
  },
  actionButton: {
    backgroundColor: theme.colors.choiceHover,
    padding: 18,
    borderRadius: 0,
    marginBottom: 16,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.textLight,
    ...Platform.select({
      web: {
        boxShadow: 'none', // Pas d'ombre moderne
      },
      default: {
        shadowColor: 'transparent',
        shadowOffset: {width: 0, height: 0},
        shadowOpacity: 0,
        shadowRadius: 0,
        elevation: 0,
      },
    }),
  },
  actionText: {
    fontSize: 17,
    fontWeight: '400',
    color: theme.colors.text,
    marginBottom: 6,
    textAlign: 'left',
    fontFamily: Fonts.reading,
  },
  actionDescription: {
    fontSize: 15,
    color: theme.colors.textMuted,
    lineHeight: 22,
    textAlign: 'left',
    fontFamily: Fonts.reading,
  },
  pathsContainer: {
    marginTop: 4,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: theme.colors.borderLight,
  },
  pathButton: {
    backgroundColor: theme.colors.surface3,
    padding: 0,
    marginBottom: 12,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 12,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 8,
      },
      default: {
        borderRadius: 12,
      },
    }),
    borderWidth: 1.5,
    borderColor: theme.colors.border,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.04,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  pathButtonHovered: {
    backgroundColor: theme.colors.choiceHover,
    borderColor: theme.colors.textLight,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)',
        transform: 'translateY(-1px)',
      },
      default: {},
    }),
  },
  pathButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 18,
    paddingLeft: 16,
    minHeight: 56, // Touch target >= 44px, ici 56px pour plus de confort
  },
  pathButtonLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pathButtonIndicator: {
    width: 4,
    height: 24,
    backgroundColor: theme.colors.textLight,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        borderBottomRightRadius: 2,
        borderBottomLeftRadius: 2,
        transition: 'all 0.2s ease',
      },
      default: {
        borderRadius: 2,
      },
    }),
    flexShrink: 0,
  },
  pathButtonIndicatorHovered: {
    backgroundColor: theme.colors.textMuted,
    width: 5,
  },
  pathButtonRight: {
    marginLeft: 12,
    flexShrink: 0,
  },
  pathArrow: {
    opacity: 0.6,
    ...Platform.select({
      web: {
        transition: 'transform 0.2s ease, opacity 0.2s ease, color 0.2s ease',
      },
    }),
  },
  pathArrowHovered: {
    opacity: 0.9, // Flèche plus visible au hover
    ...Platform.select({
      web: {
        transform: 'translateX(2px)', // Légère translation vers la droite
      },
      default: {},
    }),
  },
  pathButtonLast: {
    marginBottom: 0, // Pas de marge en bas pour le dernier bouton (géré par le padding de la zone)
  },
  pathText: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.text,
    flex: 1,
    textAlign: 'left',
    lineHeight: 22,
    fontFamily: Fonts.reading,
    letterSpacing: 0.1,
    ...Platform.select({
      web: {
        transition: 'color 0.2s ease',
      },
      default: {},
    }),
  },
  pathTextHovered: {
    color: theme.colors.text,
  },
  pathTextHidden: {
    opacity: 0.6, // Texte légèrement transparent pour les paths cachés
  },
  pathContainer: {
    marginBottom: 12,
  },
  pathButtonHidden: {
    opacity: 0.6, // Bouton légèrement transparent
    borderStyle: 'dashed', // Bordure pointillée
    borderWidth: 1.5,
    borderColor: '#8B7355', // Bordure beige pour les paths cachés
  },
  pathButtonIndicatorHidden: {
    opacity: 0.5, // Indicateur plus transparent
  },
  pathInfoButton: {
    marginRight: 8,
    padding: 4,
    borderRadius: 12,
    backgroundColor: theme.colors.objectBg,
  },
  description: {
    fontSize: isReadingMode 
      ? theme.fontSize.narrativeReading 
      : 18, // Plus grand pour EB Garamond
    lineHeight: isReadingMode
      ? theme.fontSize.narrativeReading * theme.lineHeight.narrativeReading
      : 18 * 1.7, // Interlignage très généreux pour EB Garamond
    color: theme.colors.text,
    fontWeight: isReadingMode ? '400' : '400', // Toujours léger pour aspect livre
    marginBottom: isReadingMode ? theme.spacing.xxl : theme.spacing.lg,
    textAlign: 'center', // Centré comme sur la maquette
    fontFamily: isReadingMode ? Fonts.reading : Fonts.narrative, // EB Garamond en mode normal
    letterSpacing: isReadingMode ? 0.3 : 0.3, // Légèrement aéré
    opacity: isReadingMode ? 0.92 : 0.95, // Légèrement plus doux
  },
  // Bouton "back" en mode éditeur - Stylé comme un path caché (pointillés)
  cheatBackPathButton: {
    backgroundColor: theme.colors.surface3,
    padding: 0,
    marginBottom: 12,
    borderRadius: 8,
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    opacity: 0.6, // Même opacity que les paths cachés
    borderStyle: 'dashed', // Pointillés comme les paths cachés
    borderWidth: 1.5,
    borderColor: '#8B7355', // Marron comme les paths cachés
    ...Platform.select({
      web: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.04,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  cheatBackPathIndicator: {
    width: 4,
    height: 24,
    backgroundColor: theme.colors.textLight,
    opacity: 0.5, // Même opacity que les indicateurs de paths cachés
    ...Platform.select({
      web: {
        transition: 'all 0.2s ease',
      },
    }),
  },
  cheatBackPathContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  cheatBackPathText: {
    fontSize: 15,
    fontWeight: '500',
    color: theme.colors.textLight,
    opacity: 0.6, // Même opacity que le texte des paths cachés
    fontFamily: Fonts.reading,
    flex: 1,
  },
  // Zone de décision dédiée - séparée du texte narratif
  decisionZone: {
    marginTop: isReadingMode ? 4 : 0,
    paddingTop: isReadingMode ? 28 : 24,
    paddingBottom: 16,
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
    borderTopWidth: 0, // Pas de bordure
    borderTopColor: 'transparent',
  },
  decisionZoneContent: {
    // Contenu de la zone de décision (pas de style supplémentaire nécessaire)
  },
  objectsContainer: {
    marginTop: 32,
    paddingTop: 20,
    paddingBottom: 12,
    backgroundColor: theme.colors.objectBg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.objectBorder,
  },
  objectsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  objectsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    fontFamily: Fonts.ui,
  },
  objectsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  objectChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: theme.colors.surface3,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 6,
        borderTopRightRadius: 4,
        borderBottomRightRadius: 6,
        borderBottomLeftRadius: 4,
      },
      default: {
        borderRadius: 6,
      },
    }),
    borderWidth: 1,
    borderColor: theme.colors.border,
    gap: 6,
  },
  objectChipImage: {
    width: 20,
    height: 20,
    borderRadius: 4,
  },
  objectChipIcon: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  objectChipText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text,
    fontFamily: Fonts.ui,
  },
  failedActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2E7D32', // Vert pour Rembobiner (comme Flutter)
    paddingVertical: 20,
    paddingHorizontal: 24,
    marginVertical: 8,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 14,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 12,
      },
      default: {
        borderRadius: 16,
      },
    }),
    minHeight: 44, // Touch target >= 44px pour mobile
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.15,
        shadowRadius: 4,
        elevation: 6,
      },
    }),
  },
  restartButton: {
    backgroundColor: '#1976D2', // Bleu pour Recommencer (comme Flutter)
  },
  failedActionButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
    fontFamily: Fonts.ui,
  },
  // Modal pour les conditions
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: theme.colors.surface3,
    borderRadius: 12,
    padding: 0,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 4},
        shadowOpacity: 0.3,
        shadowRadius: 10,
        elevation: 10,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#D4C4B0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2C2C2C',
    fontFamily: Fonts.ui,
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 16,
    backgroundColor: theme.colors.objectBg,
  },
  modalCloseText: {
    fontSize: 18,
    color: theme.colors.textLight,
    fontWeight: '600',
  },
  modalBody: {
    padding: 20,
    maxHeight: 400,
  },
  conditionItem: {
    marginBottom: 12,
    padding: 12,
    backgroundColor: theme.colors.objectBg,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.textLight,
  },
  conditionText: {
    fontSize: 14,
    color: theme.colors.text,
    fontFamily: Fonts.reading,
    lineHeight: 20,
  },
  pathsSeparatorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    marginBottom: 12,
  },
  descriptionContainer: {
    position: 'relative',
    paddingHorizontal: 0,
    paddingVertical: 0,
    paddingTop: 0, // Pas d'espace, commence directement sous le cadre
    paddingBottom: 0,
    backgroundColor: 'transparent',
    borderRadius: 0,
    marginHorizontal: 0,
    marginBottom: isReadingMode ? theme.spacing.xxl : theme.spacing.lg,
    borderWidth: 0,
  },
  objectDialogPlaceholder: {
    padding: 20,
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    margin: 16,
  },
});

export default BaseStepWidget;